import { put, takeLatest, select, delay } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import cloneDeep from 'lodash.clonedeep';
import objectivesApi from 'services/objectivesApi';
import jiraIntegrationApi from 'services/jiraIntegrationApi';
import {
  ActivityType,
  ToastActionText,
  ToastActionType,
  HeaderLeftTabs,
  QueryParam,
  StorageKey,
  ObjectiveTypes
} from 'utils/constants';
import { isObjectivePresentInListOfObjectives } from 'utils/objectives.utils';
import {
  setSelectedObjective,
  updateObjective,
  fetchSelectedObjective,
  storeRecentlyUpdatedObjectiveDetails,
  fetchObjectivesRequest,
  saveObjectiveInContextData,
  clearSelectedObjective,
  fetchKeyResultsRequest,
  updateTeamQuickFilterAttributes,
  resetPaginationData
} from 'containers/Objective/store/actions';
import analytics from '@xto10x/core/lib/analytics';
import storage from '@xto10x/storage/lib';
import jiraUtils from 'utils/jira.utils';
import {
  getChannel,
  isStrategicCycle,
  appendCustomRangeQueryParamsToRequest
} from 'utils/app.utils';
import {
  getSelectedTeamSelector,
  getSelectedObjectiveCycleSelector,
  getFilterParamsSelector,
  getDashboardFilterSelector,
  getSearchObjectiveParams,
  showIndependentObjectiveSelector
} from 'containers/Header/store/reducer';
import { getObjectivesSelector } from 'containers/Objective/store/reducer';
import { getDeviceDetailsSelector } from 'containers/App/store/reducer';
import actions, {
  fetchObjectiveActivities,
  fetchObjectiveCheckIns,
  fetchObjectiveComments,
  saveObjectiveActivities,
  saveObjectiveCheckins,
  saveObjectiveComments,
  fetchObjectiveDetails,
  getMetricDashboard,
  saveMetricDashboardInfo,
  saveDetailsPageData,
  saveDetailsPageSet1Data,
  saveDetailsPageSet2Data,
  saveJiraConnections,
  saveJiraIssues,
  saveJiraLabels,
  saveCreateConnectionRequestStatus,
  saveUserJiraConnections,
  saveQueryIssuesRequestStatus,
  saveConnectionRequestStatus,
  saveTrackProgressRequestStatus,
  fetchJiraConnections,
  saveNonUserJiraConnections,
  saveDeleteProgressTrackerRequestStatus,
  saveJiraIntegrationLatestProgress,
  saveObjectiveDetailsForJiraIntegration,
  saveAddReaction,
  saveRemoveReaction,
  saveRecentReactionAssociationId,
  saveViewInContextObjectiveData,
  saveObjectiveActivityDetails
} from './actions';
import {
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  resetSearchParamsAction,
  handleApiCallError,
  updateIsAnnualGoalsSpace
} from '../../App/store/actions';
import {
  saveSelectedTeam,
  setDashboardFilters,
  setFilters,
  setShowIndependentObjectives
} from '../../Header/store/actions';
import { AnalyticsCategory } from '../../../utils/analytics.constants';
import { getSelectedObjectiveSelector } from '../../Objective/store/reducer';
import { saveObjectives } from '../../Objective/store/actions';

function* updateObjectiveDetailsWorker({
  objective,
  requestBody,
  updateSelectedObjective,
  updateObjDetails
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjective(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      if (updateObjDetails)
        yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(showAlertMessage('success', 'Changes saved'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_objective_details_worker', e);
  }
}

function* createObjectiveDuplicateWorker({
  objective,
  requestBody,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    yield put(showAlertMessage('loading', 'Copying Objective...'));
    const duplicate_objective_data = yield objectivesApi.createObjectiveDuplicate(
      objective.objective_id,
      requestBody
    ); // gets the duplicated objective data
    if (updateSelectedObjective) {
      const { data } = yield objectivesApi.getObjectiveDetail(
        objective.objective_id
      );
      yield put(updateObjective(data));
      yield put(fetchSelectedObjective(objective.objective_id));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    const alertAction = {
      actionText: ToastActionText.VIEW,
      actionType: ToastActionType.HIGHLIGHT_OBJECTIVE,
      actionData: {
        ...duplicate_objective_data.data,
        isNewlyCreatedObj: !updateSelectedObjective
      }
    };
    yield put(
      showAlertMessage('success', 'Objective copied successfully', alertAction)
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('create_objective_duplicate_worker', e);
  }
}

function* checkIfObjectiveDuplicateExists({
  objective,
  requestBody,
  callback
}) {
  try {
    const { data } = yield objectivesApi.checkIsObjectiveDuplicated(
      objective.objective_id,
      requestBody
    );
    if (data.duplicated) {
      callback(true);
    }
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('check_objective_duplicate_exists_worker', e);
  }
}

function* updateObjectiveTypeWorker({
  objective,
  requestBody,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjectiveType(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    yield put(updateTeamQuickFilterAttributes());
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Changes saved'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_objective_type_worker', e);
  }
}

function* fetchObjectiveActivitiesWorker({ objectiveId }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.getObjectiveActivity(objectiveId);
    yield put(saveObjectiveActivities(data)); // saving data in reducer
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objective_activities_worker', e);
  }
}

function* fetchObjectiveCommentsWorker({ objectiveId, callback }) {
  try {
    if (!callback) yield put(showAppLoading());
    const { data } = yield objectivesApi.getObjectiveComments(objectiveId, {
      type: ActivityType.OBJECTIVE_COMMENT
    });
    if (callback) {
      callback(data);
    } else {
      yield put(saveObjectiveComments(data));
    }
    if (!callback) yield put(hideAppLoading());
  } catch (e) {
    if (!callback) yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objective_comments_worker', e);
  }
}

function* fetchObjectiveCheckinsWorker({ objectiveId }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.getObjectiveComments(objectiveId, {
      type: ActivityType.OBJECTIVE_CHECK_IN
    });
    yield put(saveObjectiveCheckins(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objective_checkins_worker', e);
  }
}

function* updateObjectiveCycleWorker({
  objective,
  requestBody,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjectiveCycle(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Time Period changed'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_objective_cycle_worker', e);
  }
}

function* postObjectiveCommentWorker({ objective_id, requestBody, callback }) {
  try {
    const deviceDetails = yield select(getDeviceDetailsSelector);
    yield put(showAppLoading());
    const { data: commentData } = yield objectivesApi.postObjectiveComment(
      objective_id,
      {
        channel: getChannel(deviceDetails),
        ...requestBody
      }
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    try {
      if (
        commentData?.message &&
        requestBody?.message &&
        commentData.message !== requestBody.message
      ) {
        analytics.trackError(
          'add_comment_posted_data_mismatch',
          new Error(
            `Objective id: ${objective_id}, comment id: ${commentData.comment_id}`
          )
        );
      }
    } catch (err) {
      analytics.trackError('comment_data_mismatch_instrumentation_error', err);
    }
    if (requestBody.comment_type === ActivityType.OBJECTIVE_CHECK_IN) {
      const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
      yield put(updateObjective(data));
      yield put(fetchObjectiveCheckIns(objective_id));
      yield put(fetchObjectiveActivities(objective_id));
    } else {
      yield put(fetchObjectiveComments(objective_id));
      if (commentData?.task_model) {
        const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
        yield put(updateObjective(data));
      }
    }
    if (callback) callback(requestBody.comment_task_request);
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(
      handleApiCallError(e, {
        type: 'ADD_COMMENT',
        data: { objective_id, requestBody, callback }
      })
    );
    analytics.trackError('update_objective_cycle_worker', e);
  }
}

function* updateObjectiveCommentWorker({
  objective_id,
  checkInId,
  requestBody,
  commentRow,
  callback
}) {
  try {
    yield put(showAppLoading());
    if (commentRow.comment_type === ActivityType.OBJECTIVE_CHECK_IN) {
      const { data: commentData } = yield objectivesApi.updateObjectiveCheckIn(
        objective_id,
        checkInId,
        requestBody
      );
      yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
      try {
        if (
          commentData?.message &&
          requestBody?.message &&
          commentData.message !== requestBody.message
        ) {
          analytics.trackError(
            'update_comment_posted_data_mismatch',
            new Error(
              `Objective id: ${objective_id}, comment id: ${commentData.comment_id}`
            )
          );
        }
      } catch (err) {
        analytics.trackError(
          'comment_data_mismatch_instrumentation_error',
          err
        );
      }
      const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
      yield put(updateObjective(data));
      yield put(setSelectedObjective(data));
      yield put(fetchObjectiveCheckIns(objective_id));
      yield put(fetchObjectiveActivities(objective_id));
      yield put(getMetricDashboard(objective_id));
    } else {
      const { data } = yield objectivesApi.updateObjectiveComment(
        objective_id,
        checkInId,
        requestBody
      );
      if (
        (!data.task_model && commentRow.task_model) ||
        (data.task_model && !commentRow.task_model)
      ) {
        const { data: objectiveData } = yield objectivesApi.getObjectiveDetail(
          objective_id
        );
        yield put(updateObjective(objectiveData));
      }
      yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
      yield put(fetchObjectiveComments(objective_id));
    }
    if (callback) callback();
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(
      handleApiCallError(e, {
        type: 'UPDATE_COMMENT',
        data: { objective_id, checkInId, requestBody, commentRow, callback }
      })
    );
    analytics.trackError('update_objective_comment_worker', e);
  }
}

function* deleteCommentWorker({
  objective_id,
  id,
  activityType,
  task_model,
  cb
}) {
  try {
    yield put(showAppLoading());
    if (activityType === ActivityType.OBJECTIVE_CHECK_IN) {
      yield objectivesApi.deleteObjectiveCheckIn(objective_id, id);
      yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
      const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
      yield put(updateObjective(data));
      yield put(setSelectedObjective(data));
      yield put(fetchObjectiveDetails(objective_id));
    } else {
      yield objectivesApi.deleteObjectiveComment(objective_id, id);
      yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
      yield put(fetchObjectiveComments(objective_id));
      if (task_model) {
        const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
        yield put(updateObjective(data));
      }
    }
    if (cb) cb();
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_comment_worker', e);
  }
}

function* fetchDetailsPageDataWorker({ objectiveId }) {
  try {
    let metricDashboardData = null;
    let checkInData = null;
    let activitiesData = null;
    let commentsData = null;
    if ('Promise' in window && Promise.allSettled !== undefined) {
      const set1Data = yield Promise.allSettled([
        objectivesApi.getMetricDashboard(objectiveId),
        objectivesApi.getObjectiveComments(objectiveId, {
          type: ActivityType.OBJECTIVE_CHECK_IN
        })
      ]);
      if (set1Data[0].status === 'fulfilled')
        metricDashboardData = set1Data[0].value?.data;
      if (set1Data[1].status === 'fulfilled')
        checkInData = set1Data[1].value?.data?.comments;
      if (metricDashboardData && checkInData)
        yield put(saveDetailsPageSet1Data(metricDashboardData, checkInData));
      const set2Data = yield Promise.allSettled([
        objectivesApi.getObjectiveActivity(objectiveId),
        objectivesApi.getObjectiveComments(objectiveId, {
          type: ActivityType.OBJECTIVE_COMMENT
        })
      ]);
      if (set2Data[0].status === 'fulfilled')
        activitiesData = set2Data[0].value?.data?.activity_details;
      if (set2Data[1].status === 'fulfilled')
        commentsData = set2Data[1].value?.data?.comments;
      if (activitiesData && commentsData)
        yield put(saveDetailsPageSet2Data(activitiesData, commentsData));
    } else {
      /* Promise all settled is not supported in chrome webview */
      const { data: d1 } = yield objectivesApi.getMetricDashboard(objectiveId);
      const { data: d2 } = yield objectivesApi.getObjectiveComments(
        objectiveId,
        {
          type: ActivityType.OBJECTIVE_CHECK_IN
        }
      );
      const { data: d3 } = yield objectivesApi.getObjectiveActivity(
        objectiveId
      );
      const { data: d4 } = yield objectivesApi.getObjectiveComments(
        objectiveId,
        {
          type: ActivityType.OBJECTIVE_COMMENT
        }
      );
      metricDashboardData = d1;
      checkInData = d2.comments;
      activitiesData = d3.activity_details;
      commentsData = d4.comments;
      yield put(
        saveDetailsPageData(
          metricDashboardData,
          checkInData,
          activitiesData,
          commentsData
        )
      );
    }
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_details_page_data_worker', e);
  }
}

function* fetchObjectiveDetailsWorker({ objectiveId }) {
  try {
    const { data } = yield objectivesApi.getObjectiveActivity(objectiveId);
    const { data: comment_details } = yield objectivesApi.getObjectiveComments(
      objectiveId,
      {
        type: ActivityType.OBJECTIVE_COMMENT
      }
    );
    const { data: check_in_details } = yield objectivesApi.getObjectiveComments(
      objectiveId,
      {
        type: ActivityType.OBJECTIVE_CHECK_IN
      }
    );
    yield put(
      saveObjectiveActivityDetails(
        data.activity_details,
        comment_details.comments,
        check_in_details.comments
      )
    );
    yield put(getMetricDashboard(objectiveId));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objective_details_worker', e);
  }
}

function* updateObjectiveMetricsWorker({
  objective,
  requestBody,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjectiveMetric(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Changes saved'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_objective_metrics_worker', e);
  }
}

function* addObjectiveMetricsWorker({
  objective,
  requestBody,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.addObjectiveMetric(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Changes saved'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('add_objective_metrics_worker', e);
  }
}

function* getMetricDashboardWorker({ objective_id, callback }) {
  try {
    const { data } = yield objectivesApi.getMetricDashboard(objective_id);
    if (callback) callback(data);
    else {
      yield put(saveMetricDashboardInfo(data));
    }
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_objective_metrics_dashboard_worker', e);
  }
}

function* deleteMetricWorker({ objective, updateSelectedObjective }) {
  try {
    const { data } = yield objectivesApi.deleteMetric(objective.objective_id);
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    const obj = {
      ...data,
      rootObjectiveIndex: objective.rootObjectiveIndex
    };
    yield put(updateObjective(obj));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(showAlertMessage('success', 'Metric deleted'));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_objective_metrics_worker', e);
  }
}

function* triggerRealtimeUpdateActivity({ objective_id }) {
  try {
    yield put(fetchObjectiveDetails(objective_id));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_activity_realtime_event', e);
  }
}

function* triggerRealtimeUpdateComments({ objective_id }) {
  try {
    yield put(fetchObjectiveComments(objective_id));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('refresh_objective_comments_realtime_event', e);
  }
}

function* triggerRealtimeUpdateCheckins({ objective_id }) {
  try {
    yield put(fetchObjectiveCheckIns(objective_id));
    yield put(getMetricDashboard(objective_id));
    yield put(fetchObjectiveActivities(objective_id));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('refresh_objective_checkins_realtime_event', e);
  }
}

function* fetchJiraConnectionsWorker({ user_reference_id }) {
  try {
    yield put(saveConnectionRequestStatus(1));
    yield put(showAppLoading());
    const { data } = yield jiraIntegrationApi.getAllConnections();
    const { connections } = data;
    if (connections) yield put(saveJiraConnections(connections));
    yield put(hideAppLoading());
    if (user_reference_id) {
      const userConnections = jiraUtils.getUserConnections(
        connections,
        user_reference_id
      );
      const nonUserConnections = jiraUtils.getNonUserConnections(
        connections,
        user_reference_id
      );
      yield put(saveUserJiraConnections(userConnections));
      yield put(saveNonUserJiraConnections(nonUserConnections));
      yield put(saveConnectionRequestStatus(2));
    } else yield put(saveConnectionRequestStatus(2));
  } catch (e) {
    yield put(hideAppLoading());
    yield put(saveConnectionRequestStatus(3));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_jira_connections', e);
  }
}

function* fetchJiraIssuesWorker({ jira_id, query }) {
  try {
    yield put(saveQueryIssuesRequestStatus(1));
    yield put(showAppLoading());
    const { data } = yield jiraIntegrationApi.getAllIssues(jira_id, query);
    const { issues } = data;
    if (issues) yield put(saveJiraIssues(issues));
    if (issues.length) yield put(saveQueryIssuesRequestStatus(2));
    else yield put(saveQueryIssuesRequestStatus(3));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(saveQueryIssuesRequestStatus(3));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_jira_issues', e);
  }
}

function* fetchJiraLabelsWorker({ jira_id }) {
  try {
    yield put(showAppLoading());
    const { data } = yield jiraIntegrationApi.getAllLabels(jira_id);
    const { labels } = data;
    yield put(saveJiraLabels(labels));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_jira_labels', e);
  }
}

function* createJiraConnectionWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    yield put(saveCreateConnectionRequestStatus(1));
    yield jiraIntegrationApi.createConnection(requestBody);
    yield put(showAlertMessage('success', 'Connection created successfully'));
    yield put(saveCreateConnectionRequestStatus(2));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(saveCreateConnectionRequestStatus(3));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('jira_create_connection', e);
  }
}

function* trackProgressWithJiraWorker({ requestBody, jiraId, update }) {
  try {
    yield put(showAppLoading());
    yield put(saveTrackProgressRequestStatus(1));
    if (!update) yield jiraIntegrationApi.trackProgress(requestBody, jiraId);
    else yield jiraIntegrationApi.updateTracker(requestBody, jiraId);
    const { data } = yield objectivesApi.getObjectiveDetail(
      requestBody.objective_id
    );
    yield put(saveObjectiveDetailsForJiraIntegration(data));
    yield put(saveTrackProgressRequestStatus(2));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(saveTrackProgressRequestStatus(3));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('track_progress_with_jira', e);
  }
}

function* deleteJiraProgressTrackerWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    yield put(saveDeleteProgressTrackerRequestStatus(1));
    yield jiraIntegrationApi.deleteTracker(requestBody);
    const { data } = yield objectivesApi.getObjectiveDetail(
      requestBody.objective_id
    );
    yield put(saveObjectiveDetailsForJiraIntegration(data));
    yield put(saveDeleteProgressTrackerRequestStatus(2));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(saveDeleteProgressTrackerRequestStatus(3));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_jira_progress_tracker', e);
  }
}

function* triggerManualJiraProgressUpdateWorker({ objective_id, version }) {
  try {
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    yield put(showAppLoading());
    yield jiraIntegrationApi.updateObjectiveProgress({ objective_id, version });
    const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
    yield put(updateObjective(data));
    yield put(setSelectedObjective(data));
    yield put(fetchObjectiveActivities(objective_id));
    yield put(fetchObjectiveCheckIns(objective_id));
    yield put(getMetricDashboard(objective_id));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_jira_progress', e);
  }
}

function* triggerManualJiraProgressUpdateForCheckInModalWorker({
  objective_id,
  version,
  callback
}) {
  try {
    yield put(showAppLoading());
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    yield jiraIntegrationApi.updateObjectiveProgress({ objective_id, version });

    const { data: objectiveData } = yield objectivesApi.getObjectiveDetail(
      objective_id
    );

    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));

    yield put(updateObjective(objectiveData));
    if (callback) {
      callback(objectiveData);
    }

    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_jira_progress_in_check_in_modal', e);
  }
}

function* updateJiraConnectionWorker({ jiraId, connection_name }) {
  try {
    yield put(showAppLoading());
    yield jiraIntegrationApi.updateConnection(jiraId, connection_name);
    yield put(hideAppLoading());
    yield put(fetchJiraConnections());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('edit_jira_connection', e);
  }
}

function* deleteJiraConnectionWorker({ jiraId }) {
  try {
    yield put(showAppLoading());
    yield jiraIntegrationApi.deleteConnection(jiraId);
    yield put(showAlertMessage('success', 'Connection deleted successfully'));
    yield put(hideAppLoading());
    yield put(fetchJiraConnections());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_jira_connection', e);
  }
}

function* getJiraIntegrationLatestProgressWorker({ requestBody }) {
  try {
    yield put(showAppLoading());
    const { data } = yield jiraIntegrationApi.getObjectiveProgress(requestBody);
    yield put(saveJiraIntegrationLatestProgress({ ...data, ...requestBody }));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_jira_latest_progress', e);
  }
}

function* getObjectiveDetailsForJiraIntegrationWorker({ objective_id }) {
  try {
    yield put(showAppLoading());
    const data = yield objectivesApi
      .getObjectiveDetail(objective_id)
      .then(res => res.data);
    yield put(saveObjectiveDetailsForJiraIntegration(data));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_objective_details_for_jira_integration', e);
  }
}

function* addReactionWorker({ data: { emoji_id, requestBody, userId } }) {
  try {
    yield put(
      saveAddReaction({
        emoji_id,
        ...requestBody,
        userId
      })
    );
    yield objectivesApi.addReaction(emoji_id, requestBody);
    yield put(saveRecentReactionAssociationId(requestBody.association_id));
  } catch (e) {
    yield put(
      saveRemoveReaction({
        emoji_id,
        ...requestBody,
        userId
      })
    );
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('add_reactions_worker', e);
  }
}

function* removeReactionWorker({ data: { emoji_id, requestBody, userId } }) {
  try {
    yield put(
      saveRemoveReaction({
        emoji_id,
        ...requestBody,
        userId
      })
    );
    yield objectivesApi.removeReaction(emoji_id, requestBody);
    yield put(saveRecentReactionAssociationId(requestBody.association_id));
  } catch (e) {
    yield put(
      saveAddReaction({
        emoji_id,
        ...requestBody,
        userId
      })
    );
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('remove_reactions_worker', e);
  }
}

function* initiateViewInContextRequestFromParamWorker({ objective_id }) {
  try {
    yield put(showAppLoading());
    const { data: objectiveDetails } = yield objectivesApi.getObjectiveDetail(
      objective_id
    );
    const selectedObjectiveCycle = yield select(
      getSelectedObjectiveCycleSelector
    );
    const selectedView = yield select(getSelectedTeamSelector);
    const objectivesList = yield select(getObjectivesSelector);
    const obj = cloneDeep(objectiveDetails);
    const showIndependentObjectivesOnly = yield select(
      showIndependentObjectiveSelector
    );
    if (
      !objectivesList ||
      selectedObjectiveCycle.id !== obj.objective_cycle.id ||
      selectedView.tag_id !== HeaderLeftTabs[2].tag_id
    )
      return;
    yield put(clearSelectedObjective());
    if (!obj.company_objective_chain) {
      yield put(setShowIndependentObjectives(true));
    }
    const isPresentInCurrentView =
      objectivesList &&
      isObjectivePresentInListOfObjectives(objectivesList, obj, true);
    if (isPresentInCurrentView) {
      if (obj.key_results_count) {
        yield put(fetchKeyResultsRequest(obj));
        yield delay(300);
      }
      yield put(saveObjectiveInContextData(obj));
      yield put(hideAppLoading());
      return;
    }
    const requestParams = {
      okr_cycle_id: obj.objective_cycle.id,
      objective_type:
        !showIndependentObjectivesOnly && obj.company_objective_chain
          ? ObjectiveTypes.company
          : `${ObjectiveTypes.company},${ObjectiveTypes.nonAligned}`
    };
    appendCustomRangeQueryParamsToRequest(requestParams);
    yield put(resetPaginationData());
    yield put(fetchObjectivesRequest(requestParams, null, false, true));
    yield put(resetPaginationData());
    const { data: viewInContextData } = yield objectivesApi.getObjectiveContext(
      obj.objective_id
    );
    yield delay(500);
    yield put(saveObjectiveInContextData(obj, viewInContextData));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError(
      'initiate_view_in_context_request_from_param_worker',
      e
    );
  }
}

function* viewInContextRequestWorker({ objective, isLinkedObjective }) {
  try {
    yield showAppLoading();
    const selectedObjectiveCycle = yield select(
      getSelectedObjectiveCycleSelector
    );
    const selectedView = yield select(getSelectedTeamSelector);
    const filters = yield select(getFilterParamsSelector);
    const dashboardFilters = yield select(getDashboardFilterSelector);
    const searchObjectives = yield select(getSearchObjectiveParams);
    const objectivesList = yield select(getObjectivesSelector);
    const obj = cloneDeep(objective);
    // if objective is already in the view
    if (
      !filters &&
      !dashboardFilters &&
      !searchObjectives &&
      selectedView.tag_id === HeaderLeftTabs[2].tag_id &&
      isObjectivePresentInListOfObjectives(objectivesList, obj, true) &&
      !isLinkedObjective
    ) {
      yield put(clearSelectedObjective());
      if (obj.key_results_count) {
        yield put(fetchKeyResultsRequest(obj));
        yield delay(300);
      }
      yield put(saveObjectiveInContextData(obj, null, isLinkedObjective));
      yield put(hideAppLoading());
      return;
    }
    if (
      !selectedObjectiveCycle ||
      selectedObjectiveCycle.id !== objective?.objective_cycle?.id ||
      selectedView.tag_id !== HeaderLeftTabs[2].tag_id ||
      filters ||
      dashboardFilters
    ) {
      yield put(setDashboardFilters(null));
      yield put(resetSearchParamsAction());
      yield put(
        saveViewInContextObjectiveData(
          objective,
          selectedObjectiveCycle,
          selectedView
        )
      );
    }
    const { data } = yield objectivesApi.getObjectiveContext(
      objective.objective_id
    );

    yield put(saveObjectives([data]));
    yield put(setDashboardFilters(null));
    yield put(saveObjectiveInContextData(objective, data, isLinkedObjective));
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('objective_view_in_context_worker', e);
  }
}

function* viewInContextStrategicGoals({
  objective,
  isLinkedObjective,
  isContinuation
}) {
  try {
    yield showAppLoading();
    const selectedObjectiveCycle = yield select(
      getSelectedObjectiveCycleSelector
    );
    const selectedView = yield select(getSelectedTeamSelector);

    // isContinuation is true when the path changes and fetches filtered objectives
    // else it is false when the view in context is clicked from the annual goals view
    // if it is false then we need to change route and fetch filtered objectives
    // after which we will save the context data and show the view in context
    if (!isContinuation) {
      yield put(saveSelectedTeam(HeaderLeftTabs[2]));
      if (
        isStrategicCycle(selectedObjectiveCycle.cycle_type) &&
        !isStrategicCycle(objective?.objective_cycle?.cycle_type)
      ) {
        yield put(updateIsAnnualGoalsSpace(false));
      }
      const ORG_SUB_DOMAIN = storage.getDataFromLocal(StorageKey.ORG_INFO)
        ?.sub_domain;
      yield put(setFilters(null));
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete(QueryParam.FILTERS);
      searchParams.delete(QueryParam.OBJECTIVE_CYCLE);
      searchParams.set(
        QueryParam.CUSTOM_DATE_RANGE_START_DATE,
        new Date(objective.start_date).getTime()
      );
      searchParams.set(
        QueryParam.CUSTOM_DATE_RANGE_END_DATE,
        new Date(objective.due_date).getTime()
      );
      searchParams.set(
        QueryParam.FILTERS,
        JSON.stringify({
          owners: [objective.owner_detail.user_reference_id]
        })
      );
      yield put(
        push(`/${ORG_SUB_DOMAIN}/objectives?${searchParams.toString()}`, {
          actionKey: actions.STRATEGIC_GOALS_VIEW_IN_CONTEXT_REQUEST,
          objective_id: objective.objective_id
        })
      );
    } else {
      // continue from here when the event is triggered from Home/views/index.js
      // which signifies we want to view in context
      const { data: objectiveDetails } = yield objectivesApi.getObjectiveDetail(
        objective
      );
      if (!objectiveDetails.company_objective_chain) {
        yield put(setShowIndependentObjectives(true));
      }
      yield put(resetPaginationData());
      yield put(setDashboardFilters(null));
      yield put(resetSearchParamsAction());
      yield put(
        saveViewInContextObjectiveData(
          objectiveDetails,
          selectedObjectiveCycle,
          selectedView
        )
      );
      yield put(clearSelectedObjective());
      const { data: data2 } = yield objectivesApi.getObjectiveContext(
        objectiveDetails.objective_id
      );
      yield put(
        saveObjectiveInContextData(
          objectiveDetails,
          data2,
          isLinkedObjective,
          true
        )
      );
    }
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('strategic_goals_view_in_context', e);
  }
}

function* acknowledgeKRChangeAutoRollUpWorker({ objective, callback }) {
  try {
    const { data } = yield objectivesApi.acknowledgeKRChangeForAutoRollUp(
      objective.objective_id,
      objective.version
    );

    const isSelectedObjectiveDetailsPanelOpen = yield select(
      getSelectedObjectiveSelector
    );
    if (callback)
      yield put(showAlertMessage('success', 'Auto roll up changes accepted!'));
    yield put(updateObjective(data));
    if (isSelectedObjectiveDetailsPanelOpen) {
      yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
      yield put(setSelectedObjective(data));
    }
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'ack_auto_roll_up_kr_changes'
    );
    if (callback) callback();
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('ack_auto_roll_up_kr_changes_worker', e);
  }
}

export default function* defaultSaga() {
  yield takeLatest(
    actions.TRIGGER_MANUAL_JIRA_PROGRESS_UPDATE_FOR_CHECK_IN_MODAL,
    triggerManualJiraProgressUpdateForCheckInModalWorker
  );
  yield takeLatest(
    actions.UPDATE_OBJECTIVE_DETAILS,
    updateObjectiveDetailsWorker
  );
  yield takeLatest(actions.UPDATE_OBJECTIVE_TYPE, updateObjectiveTypeWorker);
  yield takeLatest(
    actions.FETCH_OBJECTIVE_ACTIVITIES,
    fetchObjectiveActivitiesWorker
  );
  yield takeLatest(
    actions.FETCH_OBJECTIVE_COMMENTS,
    fetchObjectiveCommentsWorker
  );
  yield takeLatest(
    actions.FETCH_OBJECTIVE_CHECKINS,
    fetchObjectiveCheckinsWorker
  );
  yield takeLatest(actions.POST_OBJECTIVE_COMMENT, postObjectiveCommentWorker);
  yield takeLatest(
    actions.UPDATE_OBJECTIVE_COMMENT,
    updateObjectiveCommentWorker
  );
  yield takeLatest(actions.UPDATE_OBJECTIVE_CYCLE, updateObjectiveCycleWorker);
  yield takeLatest(actions.DELETE_OBJECTIVE_COMMENT, deleteCommentWorker);
  yield takeLatest(actions.FETCH_DETAILS_PAGE_DATA, fetchDetailsPageDataWorker);
  yield takeLatest(
    actions.FETCH_OBJECTIVE_DETAILS,
    fetchObjectiveDetailsWorker
  );
  yield takeLatest(
    actions.CREATE_OBJECTIVE_DUPLICATE,
    createObjectiveDuplicateWorker
  );
  yield takeLatest(
    actions.UPDATE_OBJECTIVE_METRICS,
    updateObjectiveMetricsWorker
  );
  yield takeLatest(actions.ADD_OBJECTIVE_METRICS, addObjectiveMetricsWorker);
  yield takeLatest(actions.GET_METRIC_DASHBOARD, getMetricDashboardWorker);
  yield takeLatest(actions.DELETE_METRIC, deleteMetricWorker);
  yield takeLatest(
    actions.UPDATE_ACTIVITY_REALTIME_EVENT,
    triggerRealtimeUpdateActivity
  );
  yield takeLatest(
    actions.UPDATE_COMMENTS_REALTIME_EVENT,
    triggerRealtimeUpdateComments
  );
  yield takeLatest(
    actions.UPDATE_CHECKINS_REALTIME_EVENT,
    triggerRealtimeUpdateCheckins
  );
  yield takeLatest(
    actions.CHECK_IF_OBJECTIVE_DUPLICATE_EXISTS,
    checkIfObjectiveDuplicateExists
  );
  yield takeLatest(actions.FETCH_JIRA_CONNECTIONS, fetchJiraConnectionsWorker);
  yield takeLatest(actions.FETCH_JIRA_ISSUES, fetchJiraIssuesWorker);
  yield takeLatest(actions.FETCH_JIRA_LABELS, fetchJiraLabelsWorker);
  yield takeLatest(actions.CREATE_JIRA_CONNECTION, createJiraConnectionWorker);
  yield takeLatest(
    actions.TRACK_PROGRESS_WITH_JIRA,
    trackProgressWithJiraWorker
  );
  yield takeLatest(
    actions.TRIGGER_MANUAL_JIRA_PROGRESS_UPDATE,
    triggerManualJiraProgressUpdateWorker
  );
  yield takeLatest(actions.UPDATE_JIRA_CONNECTION, updateJiraConnectionWorker);
  yield takeLatest(actions.DELETE_JIRA_CONNECTION, deleteJiraConnectionWorker);
  yield takeLatest(
    actions.DELETE_JIRA_PROGRESS_TRACKER,
    deleteJiraProgressTrackerWorker
  );
  yield takeLatest(
    actions.GET_JIRA_INTEGRATION_LATEST_PROGRESS,
    getJiraIntegrationLatestProgressWorker
  );
  yield takeLatest(
    actions.GET_OBJECTIVE_DETAILS,
    getObjectiveDetailsForJiraIntegrationWorker
  );
  yield takeLatest(actions.ADD_REACTION, addReactionWorker);
  yield takeLatest(actions.REMOVE_REACTION, removeReactionWorker);
  yield takeLatest(actions.VIEW_IN_CONTEXT_REQUEST, viewInContextRequestWorker);
  yield takeLatest(
    actions.INITIATE_VIEW_IN_CONTEXT_REQUEST_FROM_PARAM,
    initiateViewInContextRequestFromParamWorker
  );
  yield takeLatest(
    actions.STRATEGIC_GOALS_VIEW_IN_CONTEXT_REQUEST,
    viewInContextStrategicGoals
  );
  yield takeLatest(
    actions.ACKNOWLEDGE_AUTO_ROLL_UP_KR_CHANGE,
    acknowledgeKRChangeAutoRollUpWorker
  );
}
